import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RelationshipDiscoveryFilters } from "../components/profile/RelationshipDiscoveryFilters";

const API_BASE = window.location.origin;

export default function DiscoveryPreferencesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    age_min: "",
    age_max: "",
    genders: [],
    show_couples: true,  // NEW: Show couples toggle (default ON if "couple" was in genders)
    couple_types: [],  // NEW: Filter by couple composition
    relationship_styles: [],
    min_relationship_score: null,
    show_all_styles: false,
    match_strictness: "balanced",  // NEW: balanced or highly_compatible
    has_children_filter: null,     // NEW: children filter
    homeschooling_filter: null,    // NEW: homeschooling filter
    distance_miles: 25,            // NEW: distance radius (1-100, 100 = "100+")
    disable_relationship_compatibility_filters: false,  // PRO: disable goals + household filtering
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch current preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/api/me/discover-preferences`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          // Derive show_couples from whether "couple" is in genders (for backwards compatibility)
          const hasCoupleGender = (data.genders || []).includes("couple");
          setForm({
            age_min: data.age_min || "",
            age_max: data.age_max || "",
            genders: (data.genders || []).filter(g => g !== "couple"),  // Remove "couple" from genders
            show_couples: data.show_couples !== undefined ? data.show_couples : hasCoupleGender,
            couple_types: data.couple_types || [],
            relationship_styles: data.relationship_styles || [],
            min_relationship_score: data.min_relationship_score || null,
            show_all_styles: data.show_all_styles || false,
            match_strictness: data.match_strictness || "balanced",
            has_children_filter: data.has_children_filter || null,
            homeschooling_filter: data.homeschooling_filter || null,
            distance_miles: data.distance_miles ?? 25,
            disable_relationship_compatibility_filters: data.disable_relationship_compatibility_filters || false,
          });
        } else {
          console.error("Failed to fetch preferences:", res.status);
        }
      } catch (err) {
        console.error("Error fetching preferences:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        age_min: form.age_min ? parseInt(form.age_min) : null,
        age_max: form.age_max ? parseInt(form.age_max) : null,
        genders: form.genders,
        show_couples: form.show_couples,
        couple_types: form.couple_types,
        // FIX: When "show all styles" is enabled, don't send relationship_styles filter
        // This ensures backend doesn't accidentally apply style filtering
        relationship_styles: form.show_all_styles ? [] : form.relationship_styles,
        min_relationship_score: form.min_relationship_score,
        show_all_styles: form.show_all_styles,
        match_strictness: form.match_strictness,
        has_children_filter: form.has_children_filter,
        homeschooling_filter: form.homeschooling_filter,
        distance_miles: form.distance_miles,
        disable_relationship_compatibility_filters: form.disable_relationship_compatibility_filters,
      };

      const res = await fetch(`${API_BASE}/api/me/discover-preferences`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || "Failed to save preferences");
      }
    } catch (err) {
      console.error("Error saving preferences:", err);
      setError("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleGenderToggle = (gender) => {
    setForm((prev) => ({
      ...prev,
      genders: prev.genders.includes(gender)
        ? prev.genders.filter((g) => g !== gender)
        : [...prev.genders, gender],
    }));
  };

  const handleStyleToggle = (style) => {
    setForm((prev) => ({
      ...prev,
      relationship_styles: prev.relationship_styles.includes(style)
        ? prev.relationship_styles.filter((s) => s !== style)
        : [...prev.relationship_styles, style],
    }));
  };

  const handleCoupleTypeToggle = (coupleType) => {
    setForm((prev) => ({
      ...prev,
      couple_types: prev.couple_types.includes(coupleType)
        ? prev.couple_types.filter((ct) => ct !== coupleType)
        : [...prev.couple_types, coupleType],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center surface-page">
        <div className="w-12 h-12 rounded-full border-2 border-slate-700 dark:border-slate-300 border-t-slate-300 dark:border-t-slate-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen surface-page py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">
            Discovery Preferences
          </h1>
        </div>

        <p className="text-slate-400 text-sm mb-8">
          Set your preferences to see profiles that match what you&apos;re looking for.
          Leave fields empty to see everyone.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Age Range */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">
              Age Range
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Minimum Age
                </label>
                <input
                  type="number"
                  min="18"
                  max="100"
                  value={form.age_min}
                  onChange={(e) => setForm({ ...form, age_min: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:border-pink-500"
                  placeholder="18"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Maximum Age
                </label>
                <input
                  type="number"
                  min="18"
                  max="100"
                  value={form.age_max}
                  onChange={(e) => setForm({ ...form, age_max: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:border-pink-500"
                  placeholder="99"
                />
              </div>
            </div>
          </div>

          {/* Distance */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-2">
              Distance
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Set how far away matches can be. 100 means "100+ miles" (wide open).
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-200 text-sm">
                  Within{" "}
                  <span className="font-semibold">
                    {form.distance_miles === 100 ? "100+ miles" : `${form.distance_miles} miles`}
                  </span>
                </span>
                <span className="text-slate-500 text-xs">
                  1–100
                </span>
              </div>

              <input
                type="range"
                min={1}
                max={100}
                step={1}
                value={form.distance_miles}
                onChange={(e) =>
                  setForm({ ...form, distance_miles: parseInt(e.target.value, 10) })
                }
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                data-testid="distance-slider"
              />

              <div className="flex justify-between text-xs text-slate-500">
                <span>1 mi</span>
                <span>25 mi</span>
                <span>50 mi</span>
                <span>100+ mi</span>
              </div>
            </div>
          </div>

          {/* Genders */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">
              Show me
            </h2>
            <div className="space-y-2">
              {["man", "woman", "non-binary"].map((gender) => (
                <label
                  key={gender}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800/50 cursor-pointer transition"
                >
                  <input
                    type="checkbox"
                    checked={form.genders.includes(gender)}
                    onChange={() => handleGenderToggle(gender)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  />
                  <span className="text-slate-200 capitalize">{gender}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Show Couples Toggle */}
          <div className="bg-gradient-to-r from-pink-900/20 to-purple-900/20 border border-pink-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-50">
                  Show Couples
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Include couple profiles in your discovery feed
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.show_couples}
                  onChange={(e) => setForm({ ...form, show_couples: e.target.checked })}
                  className="sr-only peer"
                  data-testid="show-couples-toggle"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
              </label>
            </div>
          </div>

          {/* Couple Type Filter - Only shown when "Show Couples" is enabled */}
          {form.show_couples && (
            <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-50 mb-2">
                Couple Types
              </h2>
              <p className="text-sm text-slate-400 mb-4">
                Filter which types of couples you&apos;d like to see. Leave all unchecked to see all couples.
              </p>
              <div className="space-y-2">
                {[
                  { value: "mf", label: "Man + Woman" },
                  { value: "ff", label: "Woman + Woman" },
                  { value: "mixed", label: "Mixed / Other" }
                ].map((ct) => (
                  <label
                    key={ct.value}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800/50 cursor-pointer transition"
                  >
                    <input
                      type="checkbox"
                      checked={form.couple_types.includes(ct.value)}
                      onChange={() => handleCoupleTypeToggle(ct.value)}
                      data-testid={`couple-type-filter-${ct.value}`}
                      className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                    />
                    <span className="text-slate-200">{ct.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Relationship Styles */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">
              Relationship Style
            </h2>
            <div className="space-y-2">
              {["single", "couple", "poly", "mono_single", "poly_single"].map((style) => (
                <label
                  key={style}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800/50 cursor-pointer transition"
                >
                  <input
                    type="checkbox"
                    checked={form.relationship_styles.includes(style)}
                    onChange={() => handleStyleToggle(style)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  />
                  <span className="text-slate-200 capitalize">
                    {style.replace("_", " ")}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Children / Family Filter */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">
              Family Preferences
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Has Children
                </label>
                <select
                  value={form.has_children_filter || "any"}
                  onChange={(e) => setForm({ ...form, has_children_filter: e.target.value === "any" ? null : e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:border-pink-500"
                >
                  <option value="any">Any (no preference)</option>
                  <option value="yes">Yes, has children</option>
                  <option value="no">No children</option>
                  <option value="expecting">Expecting</option>
                  <option value="open">Open to children</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Homeschooling
                </label>
                <select
                  value={form.homeschooling_filter || "any"}
                  onChange={(e) => setForm({ ...form, homeschooling_filter: e.target.value === "any" ? null : e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:border-pink-500"
                >
                  <option value="any">Any (no preference)</option>
                  <option value="yes">Yes, homeschooling</option>
                  <option value="considering">Considering homeschooling</option>
                  <option value="no">Not homeschooling</option>
                </select>
              </div>
              
              <p className="text-xs text-slate-500">
                These filters help you find people with similar family situations. Leave as &quot;Any&quot; to see everyone.
              </p>
            </div>
          </div>

          {/* Match Quality Filter */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">
              Match Quality
            </h2>
            
            {/* NEW: Match Strictness Mode */}
            <div className="mb-6 pb-6 border-b border-slate-700">
              <label className="block text-sm font-medium text-slate-200 mb-3">
                Match Strictness
              </label>
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-slate-700 hover:border-slate-600 cursor-pointer transition">
                  <input
                    type="radio"
                    name="match_strictness"
                    value="balanced"
                    checked={form.match_strictness === "balanced"}
                    onChange={(e) => setForm({ ...form, match_strictness: e.target.value })}
                    className="mt-0.5 h-5 w-5 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-100">
                      Balanced (Recommended)
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      See more people, ranked by compatibility. Great for discovery and building connections.
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-slate-700 hover:border-slate-600 cursor-pointer transition">
                  <input
                    type="radio"
                    name="match_strictness"
                    value="highly_compatible"
                    checked={form.match_strictness === "highly_compatible"}
                    onChange={(e) => setForm({ ...form, match_strictness: e.target.value })}
                    className="mt-0.5 h-5 w-5 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-100">
                      Highly Compatible Only
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Only show matches with 60%+ compatibility. More selective, fewer but stronger matches.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Hide Low Compatibility */}
              <div className="flex items-start gap-3">
                <input
                  id="hide-low-compat"
                  type="checkbox"
                  className="mt-0.5 h-5 w-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  checked={form.min_relationship_score === 30}
                  onChange={(e) => 
                    setForm({ ...form, min_relationship_score: e.target.checked ? 30 : null })
                  }
                />
                <label htmlFor="hide-low-compat" className="flex-1">
                  <div className="text-sm text-slate-200">
                    Hide low-compatibility matches{" "}
                    <span className="font-semibold">(below 30%)</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    You&apos;ll still see minimal match profiles if this is off, but highly
                    compatible matches will always show first.
                  </div>
                </label>
              </div>

              {/* Show All Relationship Styles */}
              <div className="flex items-start gap-3">
                <input
                  id="show-all-styles"
                  type="checkbox"
                  className="mt-0.5 h-5 w-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  checked={form.show_all_styles}
                  onChange={(e) => 
                    setForm({ ...form, show_all_styles: e.target.checked })
                  }
                  data-testid="show-all-styles-toggle"
                />
                <label htmlFor="show-all-styles" className="flex-1">
                  <div className="text-sm text-slate-200">
                    Show all relationship styles{" "}
                    <span className="font-semibold">(disable filtering)</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    See everyone regardless of their relationship style. Useful for exploring 
                    different relationship structures.
                  </div>
                </label>
              </div>

              {/* PRO: Disable Relationship Compatibility Filters */}
              <div className="flex items-start gap-3">
                <input
                  id="disable-rel-compat"
                  type="checkbox"
                  className="mt-0.5 h-5 w-5 rounded border-slate-600 bg-slate-800 text-pink-600 focus:ring-pink-500 focus:ring-offset-slate-900"
                  checked={form.disable_relationship_compatibility_filters}
                  onChange={(e) => 
                    setForm({ ...form, disable_relationship_compatibility_filters: e.target.checked })
                  }
                  data-testid="disable-rel-compat-toggle"
                />
                <label htmlFor="disable-rel-compat" className="flex-1">
                  <div className="text-sm text-slate-200">
                    Show all goals &amp; household types{" "}
                    <span className="font-semibold">(disable compatibility filtering)</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    See everyone regardless of their relationship goals or household preferences. 
                    Useful when you want to explore all profiles without compatibility constraints.
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="p-4 bg-red-900/30 border border-red-700/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-900/30 border border-green-700/50 rounded-lg">
              <p className="text-green-400 text-sm">
                ✓ Preferences saved successfully!
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 bg-pink-600 hover:bg-pink-500 disabled:bg-pink-600/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
        </form>

        {/* NEW: Relationship Discovery Filters (Poly/Polygamy Support) */}
        <div className="mt-8">
          <RelationshipDiscoveryFilters 
            token={localStorage.getItem("token")}
            onSave={() => {
              setSuccess(true);
              setTimeout(() => setSuccess(false), 3000);
            }}
          />
        </div>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-slate-900/60 border border-slate-800 rounded-lg">
          <p className="text-xs text-slate-500">
            💡 Tip: Your preferences will be applied to Quick Match immediately.
            Leave options unchecked to see all available profiles.
          </p>
        </div>
      </div>
    </div>
  );
}
